import type { DropdownOption, ListSlotProps } from '../types';

/**
 * Default list slot for VNDropdown.
 *
 * Renders a flat `<ul role="listbox">` with accessible `<li role="option">` items.
 * In multi-select mode each item includes a decorative checkbox.
 * Satisfies `ListSlotProps` so it can be replaced by a custom component.
 */
const VNDropdownList = ({
  options,
  selectedIds,
  onSelect,
  multiple = false,
  focusedIndex,
  listboxId,
  optionIdPrefix,
}: ListSlotProps) => {
  return (
    <ul id={listboxId} role="listbox" aria-multiselectable={multiple || undefined} className="vn-dropdown-list">
      {options.length === 0 && (
        <li role="option" aria-selected={false} aria-disabled="true" className="vn-dropdown-list__empty">
          No options available
        </li>
      )}

      {options.map((option: DropdownOption, index: number) => {
        const isSelected = selectedIds.includes(option.id);
        const isFocused = index === focusedIndex;

        return (
          <li
            key={option.id}
            id={`${optionIdPrefix}-${index}`}
            role="option"
            aria-selected={isSelected}
            className={[
              'vn-dropdown-list__option',
              isSelected ? 'vn-dropdown-list__option--selected' : '',
              isFocused ? 'vn-dropdown-list__option--focused' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onSelect(option)}
            // Prevent the mousedown from stealing focus away from the trigger button
            onMouseDown={(e) => e.preventDefault()}
          >
            {multiple && (
              <input
                type="checkbox"
                readOnly
                checked={isSelected}
                className="vn-dropdown-list__checkbox"
                // Hidden from AT — aria-selected on the option conveys the state
                aria-hidden="true"
                tabIndex={-1}
              />
            )}
            {option.icon && option.iconAlign !== 'right' && (
              <span className="vn-dropdown-list__icon vn-dropdown-list__icon--left">{option.icon}</span>
            )}
            <span className="vn-dropdown-list__label">{option.label}</span>
            {option.icon && option.iconAlign === 'right' && (
              <span className="vn-dropdown-list__icon vn-dropdown-list__icon--right">{option.icon}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default VNDropdownList;
